import { Component, EventEmitter, forwardRef, Input, OnDestroy, OnInit, Output, ElementRef, ViewChild } from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';

import { CustomElement } from '../../classes';
import { FileItem, FileUploader, FilterFunction, Headers, ParsedResponseHeaders } from 'ng2-file-upload';
import { ValidationService } from '../../../form/services/validation.service';
import { ConfigService } from '../../../../config/config.service';
import { AuthenticationService } from '../../../../authentication/services/authentication.service';

export const CUSTOM_INPUT_CONTROL_VALUE_ACCESSOR: any = {
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => MediaComponent),
    multi: true
};

@Component({
    selector: 'chassis-media',
    templateUrl: './media.component.html',
    styleUrls: ['./media.component.scss'],
    providers: [CUSTOM_INPUT_CONTROL_VALUE_ACCESSOR]
})
export class MediaComponent extends CustomElement implements OnInit, OnDestroy {

    @ViewChild('input') input: ElementRef;

    @Input() settings = {};

    @Input() mimeType: Array<string>;

    @Input() fileType: Array<string>;

    @Input() autoUpload: boolean;

    @Input() filters: Array<FilterFunction>;

    @Input() headers: Array<Headers>;

    @Input() method: string;

    @Input() authToken: string;

    @Input() maxFileSize: number;

    @Input() multiple;

    @Input() queueLimit: number;

    @Input() ajax: string;

    @Input() multipart: boolean;

    @Input() itemAlias: string;

    @Input() authTokenHeader: string;

    @Input() parameter: { [key: string]: any };

    protected valuePlaceholder = null;

    @Output() selected: EventEmitter<any> = new EventEmitter();

    @Output() add: EventEmitter<any> = new EventEmitter();

    @Output() remove: EventEmitter<any> = new EventEmitter();

    @Input() showDefaultSelect: boolean = true;

    @Input() showGallery: boolean = true;

    @Input() showProgress: boolean = true;

    @Input() showProgressBar: boolean = true;

    @Input() showFileDrop: boolean = true;

    @Output() uploading: EventEmitter<any> = new EventEmitter();

    @Output() doneUploading: EventEmitter<any> = new EventEmitter();

    /**
     * The FileUploader instance.
     *
     * @type {FileUploader}
     */
    protected uploader: FileUploader = new FileUploader({});

    /**
     * The uploaded files.
     *
     * @type {Array}
     */
    @Input() files = [];

    public hasBaseDropZoneOver = false;

    /**
     * Constructor.
     *
     * @param {ValidationService} validationService
     * @param configService
     * @param authService
     */
    constructor(protected validationService: ValidationService, protected config: ConfigService, protected authService: AuthenticationService) {
        super();
    }

    /**
     * OnInit.
     */
    ngOnInit() {
        this.files = this.val;

        this.addHooks();

        let apiUrl = this.config.get('api_url');
        apiUrl     = apiUrl.substr(0, -1) === '/' ? apiUrl : apiUrl + '/';

        this.ajax = apiUrl + (this.ajax ? this.ajax : 'files');

        this.authenticate();

        this.setSetting('allowedMimeType', this.mimeType);
        this.setSetting('allowedFileType', this.fileType);
        this.setSetting('autoUpload', this.autoUpload, true);
        this.setSetting('filters', this.filters);
        this.setSetting('headers', this.headers);
        this.setSetting('method', this.method);
        this.setSetting('authToken', this.authToken);
        this.setSetting('authTokenHeader', this.authTokenHeader);
        this.setSetting('maxFileSize', this.maxFileSize);
        this.setSetting('queueLimit', this.multiple ? this.queueLimit ? this.queueLimit : null : 2);
        this.setSetting('url', this.ajax);
        this.setSetting('itemAlias', this.itemAlias);
        this.setSetting('additionalParameter', this.parameter);
        this.setSetting('removeAfterUpload', true);

        this.uploader.setOptions(this.settings);

        // When we have a single file upload field, we clear the queue and add the new
        // item to the queue. This way there only can be uploaded one file at a time.
        this.uploader.onAfterAddingFile = (fileItem: FileItem) => {

            // if (!this.multiple) {
            //     this.uploader.clearQueue();
            //     this.uploader.queue = [fileItem];
            // }

            // Set the file name as the input value.
            if (this.uploader.queue.length === 1) {
                this.valuePlaceholder = fileItem.file.name;
            }
        };
    }

    public fileOverBase(e: any): void {
        this.hasBaseDropZoneOver = e;
    }

    public openFileBrowser() {
        this.input.nativeElement.click();
    }

    public triggerSelectedEvent() {

    }

    /**
     * Set the authentication http headers.
     */
    protected authenticate() {
        this.authService.tokenTrigger.subscribe(token => {
            if (token) {
                this.authToken = 'Bearer ' + token;
            } else {
                delete this.authToken;
            }
        });
    }

    /**
     * Add the hooks.
     */
    protected addHooks() {

        if (!this.files) {
            this.files = [];
        }

        // On success.
        this.uploader.onCompleteItem = (item: FileItem, response: string, status: number, headers: ParsedResponseHeaders) => {
            if (status === 200) {
                const file = JSON.parse(response)['data'];

                if (this.multiple) {
                    this.files.push(file);
                } else {
                    this.files = [file];
                }
                this.add.emit(file);
            }
        };

        /**
         * Output uploading event right before uploading
         */
        this.uploader.onBeforeUploadItem = () => {
            this.uploading.emit();
        }

        /**
         * Output done uploading event when the queue completes
         *
         */
        this.uploader.onCompleteAll = () => {
            this.val = this.files;
            this.change.emit(this.files);
            this.doneUploading.emit();
        };

        if (!this.autoUpload) {

            this.validationService.addHook((resolve, reject) => {

                this.uploader.uploadAll();

                // Continue if there are no items to upload.
                if (!this.uploader.isUploading) {
                    resolve();
                } else {
                    this.uploading.emit();
                }

                // On error.
                this.uploader.onErrorItem = (item: FileItem, response: string, status: number, headers: ParsedResponseHeaders) => {
                    reject();
                };

                this.doneUploading.subscribe(() => {
                    resolve();
                });

            });

        }
    }

    /**
     * Remove a file.
     *
     * @param item
     */
    public removeFile(item: any) {
        this.remove.emit(item);

        this.files = this.files.filter((val) => {
            return item.id !== val.id;
        });

        this.val = this.val.filter((val) => {
            return item.id !== val;
        });
    }

    /**
     * Set a setting.
     *
     * @param {string} key
     * @param value
     * @param def
     */
    protected setSetting(key: string, value: any, def?: any) {
        if (value || !value && def) {
            this.settings[key] = value ? value : def;
        }
    }

    /**
     * OnDestroy.
     */
    ngOnDestroy() {
    }

}



// WEBPACK FOOTER //
// ./libs/chassis-core/src/app/forms/elements/components/media/media.component.ts